import React, { useLayoutEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { cloneDeep, isEqual } from "lodash";
import Loader from "utils/Loader";
import CustomOptionModal from "./CustomOptionModal";
import InsightsFormTopComponent from "./InsightsFormTopComponent";
import InsightsFormTableComponent from "./InsightsFormTableComponent";
import InsightsFormHandler from "./InsightsFormHandler";

const generateRandomKey = () => `key-${Math.random().toString(36).slice(2, 11)}`;

const defaultKpiAlertRow = (defaultValues) => ({
  kpi: {
    value: "",
    display: "",
  },
  teamIds: defaultValues?.teamIds || [],
  reportOn: {
    team: true,
    user: true,
  },
  parameters: {
    value: "",
    display: "",
  },
  period: defaultValues?.period || {
    value: "",
    display: "",
  },
  interval: defaultValues?.interval || {
    value: "",
    display: "",
  },
  type: null,
  threshold: null,
  status: true,
  key: generateRandomKey(),
});

const InsightsForm = ({
  settings,
  saving,
  hasChanges,
  onSubmit,
  setHasChanges,
  selectedInsightsConfigType,
}) => {
  const [clonedSetting, setClonedSetting] = useState();
  const [initialLoading, setInitialLoading] = useState(true);
  const [customOptionModalOpen, setCustomOptionModalOpen] = useState(false);
  const [customOptionModelData, setCustomOptionModelData] = useState(null);

  const isFormIsEmpty =
    clonedSetting?.alerts?.length === 1 && clonedSetting.alerts[0]?.length === 1
      ? isEqual(
          {
            ...defaultKpiAlertRow(clonedSetting?.defaultValues),
            key: null,
          },
          { ...clonedSetting.alerts[0][0], key: null }
        ) ||
        isEqual(
          {
            ...defaultKpiAlertRow(),
            key: null,
          },
          { ...clonedSetting.alerts[0][0], key: null }
        )
      : false;

  const handleCustomOptionModalOpen = (data) => {
    setCustomOptionModelData(data);
    setCustomOptionModalOpen(true);
  };
  const handleCustomOptionModalClose = () => {
    setCustomOptionModelData(null);
    setCustomOptionModalOpen(false);
  };
  const handleCustomOptionModalSubmit = (data) => {
    const selectedValueObject =
      data.for === "parameters"
        ? {
            value: Number(data.value) / 100,
            display: `${data.type.label} by ${data.value}%`,
            type: data.type.value,
          }
        : {
            value: Number(data.value),
            display: `${data.value} Days`,
          };

    const matchedOption = settings.dropDownOptions[data.optionToUpdate].find((option) =>
      isEqual(option, selectedValueObject)
    );

    if (!matchedOption) {
      if (settings?.dropDownOptions?.[data.optionToUpdate]) {
        settings.dropDownOptions[data.optionToUpdate].splice(
          settings.dropDownOptions[data.optionToUpdate].length - 1,
          0,
          selectedValueObject
        );
      }
    }

    setClonedSetting((prev) => {
      const clone = cloneDeep(prev);
      let current = clone;
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < data.pathToUpdate.length - 1; i++) {
        current = current[data.pathToUpdate[i]]; // Traverse deeper into the object/array
      }
      const lastKey = data.pathToUpdate[data.pathToUpdate.length - 1];

      current[lastKey] = selectedValueObject;

      return clone;
    });

    setCustomOptionModalOpen(false);
    setCustomOptionModelData(null);
    setHasChanges(true);
  };

  const convertAlerts = (alerts, defaultValues, alertsKpiOrder = {}) => {
    const rows = [];

    if (!alerts) return rows;

    Object.entries(alerts)
      .sort((a, b) => alertsKpiOrder[a[0]] - alertsKpiOrder[b[0]])
      .forEach(([kpiId, value]) => {
        if (!kpiId) return;
        const nestedRows = [];

        Object.values(value).forEach((kpiSetting) => {
          const matchedKpi = settings?.dropDownOptions?.kpis?.find(
            (option) => option.value === Number(kpiId)
          );
          const matchedTeams = settings?.dropDownOptions?.teamIds?.filter((option) =>
            kpiSetting.teamIds.includes(option.value)
          );
          let matchedPeriod = settings?.dropDownOptions?.period?.find(
            (option) => option.value === kpiSetting.period
          );
          const matchedInterval = settings?.dropDownOptions?.interval?.find(
            (option) => option.value === kpiSetting.interval
          );
          let matchedParameters = settings?.dropDownOptions?.parameters?.find(
            (option) => option.value === kpiSetting.threshold && option.type === kpiSetting.type
          );

          if (!matchedPeriod) {
            matchedPeriod = {
              value: Number(kpiSetting.period),
              display: `${kpiSetting.period} Days`,
            };
            settings.dropDownOptions.period.splice(
              settings.dropDownOptions.period.length - 1,
              0,
              matchedPeriod
            );
          }
          if (!matchedParameters) {
            matchedParameters = {
              value: Number(kpiSetting.threshold),
              display: `${kpiSetting.type.charAt(0).toUpperCase() + kpiSetting.type.slice(1)} by ${
                kpiSetting.threshold * 100
              }%`,
              type: kpiSetting.type,
            };

            settings.dropDownOptions.parameters.splice(
              settings.dropDownOptions.parameters.length - 1,
              0,
              matchedParameters
            );
          }

          nestedRows.push({
            ...kpiSetting,
            kpi: matchedKpi,
            teamIds: matchedTeams,
            parameters: matchedParameters,
            period: matchedPeriod,
            interval: matchedInterval,
            key: generateRandomKey(),
          });
        });

        rows.push(nestedRows);
      });

    if (!rows.length) {
      rows.push([defaultKpiAlertRow(defaultValues)]);
    }

    return rows;
  };

  const convertDefaultValues = (defaultValues = {}) => {
    if (defaultValues?.period) {
      const matchedDefaultPeriod = settings?.dropDownOptions?.period?.find(
        (option) => option.value === defaultValues.period
      );

      if (!matchedDefaultPeriod) {
        settings.dropDownOptions.period.splice(settings.dropDownOptions.period.length - 1, 0, {
          value: Number(defaultValues.period),
          display: `${defaultValues.period} Days`,
        });
      }
    }

    return {
      ...defaultValues,
      teamIds: settings?.dropDownOptions?.teamIds?.filter((option) =>
        defaultValues.teamIds.includes(option.value)
      ),
      period: settings?.dropDownOptions?.period?.find(
        (option) => option.value === defaultValues.period
      ),
      interval: settings?.dropDownOptions?.interval?.find(
        (option) => option.value === defaultValues.interval
      ),
    };
  };

  const calculateEnabledFlags = ({ companyEnabled = false, userEnabled = false }) => {
    const convertedFlags = {
      companyEnabled: false,
      userEnabled: false,
    };

    switch (selectedInsightsConfigType) {
      case "company":
        if (companyEnabled) {
          convertedFlags.companyEnabled = true;
          if (convertedFlags.userEnabled) convertedFlags.userEnabled = false;
        }
        break;
      case "user":
        if (userEnabled) {
          convertedFlags.userEnabled = true;
          if (convertedFlags.companyEnabled) convertedFlags.companyEnabled = false;
        }
        break;

      default:
        break;
    }

    return convertedFlags;
  };

  const setInitialClonedSetting = () => {
    const defaultValues = convertDefaultValues(settings.defaultValues);
    const convertedAlerts = convertAlerts(settings.alerts, defaultValues, settings.alertsKpiOrder);

    setClonedSetting({
      ...calculateEnabledFlags({
        companyEnabled: settings?.companyEnabled,
        userEnabled: settings?.userEnabled,
      }),
      alerts: convertedAlerts,
      defaultValues,
      deliveryMechanisms: settings.deliveryMechanisms,
    });

    setInitialLoading(false);
  };

  useLayoutEffect(() => {
    setInitialClonedSetting();
  }, [settings]);

  const { enabledAlerts, usedKpis = [] } = useMemo(
    () =>
      (clonedSetting?.alerts || []).reduce(
        (convertedValues, parentObject) => {
          if (parentObject.length)
            parentObject.forEach((alert) => {
              if (alert?.kpi?.value) convertedValues.usedKpis.push(alert.kpi.value);
              if (alert?.interval?.value) convertedValues.enabledAlerts[alert.interval.value] += 1;
            });

          return convertedValues;
        },
        {
          enabledAlerts: {
            daily: 0,
            weekly: 0,
            monthly: 0,
            kpis:
              (settings?.dropDownOptions?.kpis?.length || 0) - (clonedSetting?.alerts?.length || 0),
          },
          usedKpis: [],
        }
      ),
    [settings?.dropDownOptions, clonedSetting?.alerts]
  );

  const checkKeyDown = (e) => {
    if (e.key === "Enter") e.preventDefault();
  };

  const handleOnSubmit = (...args) => {
    const skipUserCompanyConfirmation = args[2] || false;

    const body = {
      ...calculateEnabledFlags({
        companyEnabled: clonedSetting?.companyEnabled,
        userEnabled: clonedSetting?.userEnabled,
      }),
      alerts: {},
      alertsKpiOrder: {},
      defaultValues: {
        type: null,
        period: clonedSetting.defaultValues.period?.value || null,
        teamIds: clonedSetting.defaultValues.teamIds?.length
          ? clonedSetting.defaultValues.teamIds.map((team) => team.value)
          : [],
        interval: clonedSetting.defaultValues.interval?.value || null,
        reportOn: {
          team: true,
          user: true,
        },
        threshold: null,
      },
      deliveryMechanisms: clonedSetting.deliveryMechanisms,
    };

    body.alerts = isFormIsEmpty
      ? {}
      : clonedSetting.alerts.reduce((alerts, kpiAlerts, index) => {
          if (!kpiAlerts?.[0]?.kpi?.value) return alerts;

          body.alertsKpiOrder[kpiAlerts[0].kpi.value] = index + 1;
          alerts[kpiAlerts[0].kpi.value] =
            kpiAlerts.reduce((kpi, object) => {
              kpi[
                `${object.parameters.value}-${object.period.value}-${object.parameters.type}-${object.interval.value}`
              ] = {
                period: object.period.value,
                status: object.status,
                teamIds: object.teamIds.map((team) => team.value) || [],
                interval: object.interval.value,
                reportOn: object.reportOn,
                threshold: object.parameters.value,
                type: object.parameters.type,
              };

              return kpi;
            }, {}) || {};

          return alerts;
        }, {});

    onSubmit(body, skipUserCompanyConfirmation);
  };

  const handleOnError = () => {
    if (isFormIsEmpty) handleOnSubmit();
  };

  const duplicateRow = (parentIndex) => {
    const copyClonedSetting = cloneDeep(clonedSetting);
    const newObject = defaultKpiAlertRow(clonedSetting?.defaultValues);

    if (parentIndex >= 0) {
      copyClonedSetting.alerts[parentIndex].push(newObject);
    } else {
      copyClonedSetting.alerts.push([newObject]);
    }

    setClonedSetting(copyClonedSetting);
    setHasChanges(true);
  };

  const deleteRow = (parentIndex, index) => {
    const copyClonedSetting = cloneDeep(clonedSetting);

    if (copyClonedSetting.alerts[parentIndex].length > 1) {
      copyClonedSetting.alerts[parentIndex].splice(index, 1);
    } else {
      copyClonedSetting.alerts.splice(parentIndex, 1);
    }

    if (!copyClonedSetting.alerts.length) {
      copyClonedSetting.alerts.push([defaultKpiAlertRow(clonedSetting?.defaultValues)]);
    }
    setClonedSetting(copyClonedSetting);
  };

  const methods = useForm({
    reValidateMode: "all",
  });

  const handleFormCancel = () => {
    setInitialClonedSetting();
    setHasChanges(false);
    methods.clearErrors();
  };

  return initialLoading ? (
    <Loader loading={initialLoading} />
  ) : (
    <>
      <FormProvider {...methods}>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <form
          onSubmit={methods.handleSubmit(handleOnSubmit, handleOnError)}
          onKeyDown={(e) => checkKeyDown(e)}
          autoComplete="off"
        >
          {/* Top Cards Component */}
          <InsightsFormTopComponent
            settings={settings}
            clonedSetting={clonedSetting}
            setClonedSetting={setClonedSetting}
            handleCustomOptionModalOpen={handleCustomOptionModalOpen}
            enabledAlerts={enabledAlerts}
            setHasChanges={setHasChanges}
            selectedInsightsConfigType={selectedInsightsConfigType}
            hasChanges={hasChanges}
            saving={saving}
            handleFormCancel={handleFormCancel}
            handleOnSubmit={handleOnSubmit}
          />
          {/* Table */}
          <InsightsFormTableComponent
            settings={settings}
            clonedSetting={clonedSetting}
            setClonedSetting={setClonedSetting}
            usedKpis={usedKpis}
            handleCustomOptionModalOpen={handleCustomOptionModalOpen}
            deleteRow={deleteRow}
            duplicateRow={duplicateRow}
            setHasChanges={setHasChanges}
            skipValidations={isFormIsEmpty}
          />
          {/* Form handlers */}
          <InsightsFormHandler
            hasChanges={hasChanges}
            saving={saving}
            handleFormCancel={handleFormCancel}
            styles={{ marginTop: "2rem" }}
          />
        </form>
      </FormProvider>
      {/* Custom option Modal */}
      {customOptionModalOpen ? (
        <CustomOptionModal
          open={customOptionModalOpen}
          data={customOptionModelData}
          handleClose={handleCustomOptionModalClose}
          handleSubmit={handleCustomOptionModalSubmit}
        />
      ) : null}
    </>
  );
};

export default InsightsForm;
